import { useState } from 'react'
import { Box, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { z } from 'zod'
import { format } from 'date-fns'

import { adjustExpiryDateDialogStyles as styles } from './adjust-expiry-date-dialog.styles'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { getSiteDateFormat } from '@hapana/shared/web/utils/date-format-utils'

interface AdjustExpiryDateDialogProps {
  serviceId: string
  currentExpiresAt?: string
  onAdjust: (data: { expiresAtDate: string }) => Promise<void>
}

const formSchema = z.object({
  expiresAtDate: z.string(),
})

type FormData = z.infer<typeof formSchema>

export const AdjustExpiryDateDialog = ({
  currentExpiresAt,
  onAdjust,
}: AdjustExpiryDateDialogProps) => {
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      expiresAtDate: currentExpiresAt ?? '',
    },
  })

  const onSubmit = async (data: FormData) => {
    if (!data.expiresAtDate) return
    try {
      setIsSubmitting(true)
      await onAdjust({ expiresAtDate: data.expiresAtDate })
      closeDialog()
    } catch {
      // Error handled by mutation onError (toast)
    } finally {
      setIsSubmitting(false)
    }
  }

  const dateFormat = getSiteDateFormat('short')

  const primaryAction = {
    label: t('clients.packages.adjustExpiry.confirm'),
    action: handleSubmit(onSubmit),
    disabled: isSubmitting,
    isLoading: isSubmitting,
  }

  return (
    <DialogLayout
      title={t('clients.packages.adjustExpiry.title')}
      closeType={CLOSE_TYPES.ICON}
      size={SIZES.SM}
      onClose={closeDialog}
      primaryAction={primaryAction}
    >
      <Box sx={styles.container}>
        <Controller
          name="expiresAtDate"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t('clients.packages.adjustExpiry.newExpiryDate')}
                value={field.value ? new Date(field.value + 'T12:00:00') : null}
                onChange={(date) => {
                  field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                }}
                minDate={new Date()}
                inputFormat={dateFormat}
                renderInput={(params) => <TextField {...params} fullWidth />}
                PopperProps={{ sx: { zIndex: 9999 } }}
                sx={styles.datePicker}
              />
            </LocalizationProvider>
          )}
        />
      </Box>
    </DialogLayout>
  )
}
